/**
 * Progressive REVEAL — Phase 2 of the Growth Module.
 *
 * ⭐⭐ **REVEAL IS NOT A GATE.** A surface that has not been revealed is absent
 * from its nav list. It is NOT disabled, NOT greyed out, and it remains
 * REACHABLE BY URL. Nothing here may ever refuse an action, block a create path,
 * or render a lock icon. If you find yourself writing "you must first…", stop —
 * that is the feature that locked real users out of creating projects and
 * routines at zero held on 2026-08-03, invisibly, behind `developer_mode`.
 *
 * ⭐⭐ **ABSENCE SHOWS.** Every unknown resolves to VISIBLE. There are four
 * independent ways to be visible and only one narrow way to be hidden, so any
 * bug, any unhydrated store, any unmapped surface, any missing record fails
 * OPEN. That is the exact inverse of the last attempt, whose gate needed a
 * `user_unlocks` row that nothing in the app has ever written — so the failure
 * mode was "hidden forever with no reachable requirement".
 *
 * ⭐ **No new table, no migration.** Reveal is derived from the module's own
 * step completion (already derived from artifacts) plus a HIGH-WATER set on
 * `user_identity.data`. The high-water set is what makes reveal permanent:
 * completion is derived, so deleting your only quest would otherwise un-reveal
 * the Build page. Reveal only ever grows.
 *
 * See `docs/GROWTH_MODULE_SPEC.md` §7.
 */

import {
  ALL_JOURNEY_STEPS, ACK_STEP_IDS, CALIBRATION_ACK_IDS, BRAIN_DUMP_TARGET, INBOX_PROCESSED_TARGET,
  sequenceStepStates,
} from './growthJourney';

/**
 * Surfaces that can be revealed, namespaced by the list they live in.
 *
 * ⚠️ A surface NOT in this union is always visible — that is deliberate, and it
 * is why adding a new tab to the app can never accidentally hide it.
 */
export type RevealSurface =
  /* ⚠️⚠️ `nav:dashboard` WAS HERE and was RETIRED on 2026-08-19 (owner), when
     the Roadmap LEFT THE NAV RAIL and became reachable only from Home's Growth
     Journey panel.
     ⭐⭐ HOME MUST NOW BE ALWAYS-VISIBLE, and that is not a preference — it is
     the only arrangement that works. The Growth Module LIVES INSIDE the
     Roadmap. With the Roadmap off the rail, Home is the sole route to it; had
     Home stayed revealed by `build-path` (step 4), a brand-new enrolled user
     would have had neither Home nor Roadmap and could NEVER HAVE REACHED STEP
     0. A gate on the door to the thing that opens every other door.
     ⭐ Removing the id is safe, unlike renaming one: `revealedSurfaces` is only
     ever QUERIED by surface, never iterated as truth, so the already-banked
     "nav:dashboard" string simply goes inert. Same call as `status:wallet`
     (2026-08-12) and `menu:settings` (2026-08-13) below.
     ⚠️ `STEP_WORKS_ON['work-the-day']` named this surface and was REPOINTED to
     `home:schedule`, which is where step 12's work actually happens. */
  /* ⭐⭐ `nav:dashboard` IS BACK (owner, 2026-09-29) — and it is safe now for
     the reason it was unsafe then. The Roadmap RETURNED TO THE RAIL, first
     item, never gated, and it is where onboarding lands. So the way in to step
     0 no longer runs through Home, and Home can wait for step 4 like the Home
     components it holds. `moduleReveal.test.ts` pins `nav:roadmap` as the
     always-visible way in; `resolveLandingView` falls back to the Roadmap.
     ⚠️ `nav:roadmap` and `nav:settings` stay ABSENT — always visible. */
  | 'nav:dashboard' | 'nav:calendar' | 'nav:inbox'
  | 'nav:action' | 'nav:discover'
  /* ⛔ `nav:domains` (the Domains PAGE on the rail, 2026-10-01) RETIRED 2026-10-03
     with the page itself (owner). Step 17 now opens Roadmap → Domain Progression,
     revealed by `roadmap:domains` at the same step. A stored `nav:domains` in
     anybody's `revealedSurfaces` is harmless — nothing asks about it any more. */
  // Roadmap tracks. ⚠️ `journey` and `domains` are always visible.
  | 'roadmap:achievements'
  /* ⚠️⚠️ **THE `roadmap:` PREFIX ON THE NEXT FOUR IS HISTORICAL, AND MUST STAY.**
     Assessments and Values moved Roadmap → REFLECT on 2026-08-19. The ids did
     NOT move with them, because a surface id is a STORAGE KEY: these exact
     strings are banked in `user_identity.data.revealedSurfaces`.
     ⚠️⚠️ Renaming `roadmap:assessments` → `reflect:assessments` would have hidden
     the Assessments tab PERMANENTLY for the only reveal-enrolled account. It is
     revealed by `clear-the-mind`, and `useReveal` computes only SEVEN steps —
     `clear-the-mind` is NOT one of them — so the new id could never re-earn
     what the old id had banked. (The other three WOULD have re-earned, since
     their steps are computed; the danger was not uniform, which is exactly what
     makes it easy to miss.)
     ⭐ Precisely the call already made for `roadmap:achievements` above when it
     became a Status tab: the name records where the tab used to live, and that
     is the price of never renaming a key. [[display-name-is-a-storage-key]] */
  | 'roadmap:assessments' | 'roadmap:values'
  // ⚠️ NOT the Assessments TRACK — that arrives at step 1, carrying Foundation
  // and nothing else. These two are the library's contents, in the order the
  // module needs them: the Life Map at step 2 (step 3 takes it), and the other
  // ten at step 7 (step 8 looks at Talent, step 16 takes it). Each is revealed
  // by an EARLIER step than the one that uses it — see `REVEALED_BY`.
  | 'roadmap:assessment-lifemap'
  /* ⭐⭐ **THE TALENT ASSESSMENT, SPLIT OUT OF THE LIBRARY (owner, 2026-08-21).**
     The owner asked for the library to open at step 16 instead of step 7 — and
     the walkability test refused it, correctly: step 8 TAKES the Talent
     assessment, which lived behind `roadmap:assessment-library`, so moving that
     surface to step 15 sent a reveal-enrolled user to a quiz that was not on the
     page. The split makes the existing label true: `assessment-library` is
     literally called "the REST of the assessment library". */
  | 'roadmap:assessment-talent'
  /* ⭐⭐ ONE KEY PER ASSESSMENT, FOR CALIBRATION (owner, 2026-10-05). These ten
     replace `roadmap:assessment-library`, which revealed all ten at once at step
     15. Each is now revealed by the Calibration step BEFORE the one about it, so
     the assessment is on the page the moment its step opens.
     ⚠️⚠️ `roadmap:assessment-library` IS NO LONGER A SURFACE — and must never be
     asked through `isRevealed`, which FAILS OPEN for an unmapped surface and
     would show all ten to everyone. Accounts that banked it keep all ten: the
     Assessments library reads the BANK directly (`LEGACY_LIBRARY_SURFACE`). */
  | 'roadmap:assessment-identity' | 'roadmap:assessment-life-balance'
  | 'roadmap:assessment-energetic' | 'roadmap:assessment-mind'
  | 'roadmap:assessment-job-role' | 'roadmap:assessment-career'
  | 'roadmap:assessment-personality' | 'roadmap:assessment-archetype'
  | 'roadmap:assessment-shadow' | 'roadmap:assessment-growth'
  // Discover ribbon.
  | 'discover:global' | 'discover:people' | 'discover:chats'
  | 'discover:guilds' | 'discover:marketplace'
  /* Reflect ribbon.
     ⚠️⚠️ `reflect:journal` WAS HERE (← `first-progress`, step 7) and was RETIRED
     on 2026-08-19 (owner), when the **Overview tab was removed and Journal
     became the page's root**.
     ⭐⭐ THE ROOT TAB OF A PAGE CANNOT BE REVEALABLE. `overview` was unmapped for
     exactly this reason — its comment read *"a ribbon with no tabs would be a
     page with no way back into itself"* — and it was the ONLY unmapped tab
     here. Deleting it without unmapping its successor would have left a
     reveal-enrolled user with an EMPTY ribbon before step 1, and from step 1 to
     step 7 a Journal that renders as content while having no tab to return to.
     ⚠️ Same call, and the same reason, as `nav:dashboard` earlier today: the way
     IN to a surface may never sit behind that surface's own progress.
     ⭐ Safe to remove: `revealedSurfaces` is only ever QUERIED by surface, so an
     already-banked "reflect:journal" goes inert rather than orphaning anything.
     ⚠️ `STEP_WORKS_ON['first-reflection']` named it and was dropped with it — an
     always-visible surface cannot deadlock, so the entry proved nothing. */
  /* ⚠️⚠️ `nav:journal` IS THE REFLECT PAGE, and the id is the VIEW id, not the
     word on the rail. `Navigation` builds its query as `nav:${item.id}` and the
     Reflect item's id is `journal` — so `nav:reflect` would be a string nothing
     asks about, and `isRevealed` takes a `string`, so it would NOT be a type
     error. It would simply fail OPEN and the page would stay visible while a
     test asserting the map went green. [[display-name-is-a-storage-key]]
     ⭐⭐ ADDED 2026-08-21 (owner): Reflect was ALWAYS-VISIBLE from step 0 — this
     row was unmapped, and the comment in `Navigation` said it had to be,
     because the rail was "its only door". It is not: steps 2, 3, 8, 11, 13, 18
     and 20 all carry `surface: 'journal'`, so the module opens Reflect with its
     own button whenever it sends you there. The rail is the only door for
     someone NOT in the module, and a reveal-enrolled user is by definition in
     it.
     ⚠️ THE TAB INSIDE IT STAYS UNMAPPED. `reflect:journal` was retired on
     2026-08-19 because Journal is the page's ROOT tab, and that has not
     changed: this hides the whole PAGE, and when the page arrives its root tab
     is already there. A page can be revealed; its own way back into itself
     cannot. */
  | 'nav:journal'
  /* ⚠️⚠️ **`reflect:journal` IS BACK (owner, 2026-08-21), AND THE RULE IT BROKE
     HAS CHANGED SHAPE.** It was retired on 2026-08-19 with the reasoning "the
     ROOT TAB of a page cannot be revealable" — true while the page itself was
     always visible, because gating the root left a ribbon with no way back into
     the page.
     ⭐⭐ Reflect is now revealed at step 1 CARRYING ASSESSMENTS, so the page never
     appears with an empty ribbon and the old objection no longer applies. The
     invariant is now the honest one: **a revealed page must always have at least
     one revealed tab**, and `JournalView` lands on the first VISIBLE tab rather
     than on a hard-coded root. Asserted by test, at every step of the module.
     ⭐ Re-mapping an id that was banked before is safe in the direction that
     matters: an account that banked "reflect:journal" back when it was mapped
     keeps the tab, because `revealedSurfaces` is queried, never re-derived. */
  | 'reflect:journal' | 'reflect:cycles' | 'reflect:protocol'
  /* Home's own panels.
     ⭐⭐ HOME NOW ARRIVES AT STEP 0 AND GROWS. It is always-visible (see the
     retired `nav:dashboard` above), so it can no longer arrive whole — an
     empty command centre on day one would be a page about a day the user has
     not planned yet. Each panel is therefore mapped to the step that produces
     the thing it displays: actions once there are actions, the schedule once
     there is a template, the clock and progress once the day is being worked.
     ⚠️ The Growth Journey panel and the greeting are deliberately NOT here.
     They are the reason Home is reachable at all, and gating them would
     reintroduce the deadlock that retiring `nav:dashboard` just removed.
     ⚠️ The Motivator/quote is unmapped too — it needs no prior work to mean
     something.
     ⚠️⚠️ `home:rapidlog` WAS HERE and was RETIRED on 2026-08-19 with the
     `RapidLogPanel` it named (owner: the inline capture goes with it). Reflect
     is now a rail destination in its own right, so Home no longer has to be its
     door. Its `SURFACE_LABELS` entry read "Home's Rapid Log" and was printed to
     users under step 11's "Also opens" — keeping an inert id would go on
     promising a panel that no longer exists, and prose is a promise too. Same
     call as `status:wallet` and `menu:settings`. */
  | 'home:inbox' | 'home:actions' | 'home:schedule' | 'home:clock'
  /* ⭐ Focus and Free mode — the two mode buttons in `SchedulePanel`'s header.
     ⚠️ NOT the same thing as `home:clock`, which gates the clock and the
     day-progress ring. See `REVEALED_BY`. */
  | 'home:day-mode'
  // The rail's own furniture: the assistant button, the wallet widget, and the
  // avatar block (which is the ONLY route to Status, so hiding it hides Status).
  | 'nav:assistant' | 'nav:wallet' | 'nav:avatar'
  /* ⚠️⚠️ `status:wallet` WAS HERE and was RETIRED on 2026-08-12 with the Status
     Wallet tab it named. Its SURFACE_LABELS entry read "the Wallet tab", which
     the step footer prints under "Also opens" — keeping an inert surface would
     have gone on promising a tab that no longer exists, and prose is a promise
     too. `nav:wallet` already covers the wallet, now as a view.
     ⭐ Safe to remove, unlike a renamed id: `revealedSurfaces` is only ever
     QUERIED by surface (never iterated as truth), so an already-banked
     "status:wallet" string is inert rather than orphaning anything. */
  /* ⚠️⚠️ `menu:settings` WAS HERE and was RETIRED on 2026-08-13 (owner).
     Settings is now visible from the start. It was revealed by `personalize`
     (step 9), which meant a brand-new user could not change their avatar, their
     name, their theme or their language until nine steps in — settings people
     reach for on day one, long before the module gets to them.
     ⚠️ Its `SURFACE_LABELS` entry read "Settings" and was printed to users
     under step 9's "Also opens"; step 9 no longer has anything to do with
     settings, so leaving it would have promised a thing that is no longer
     given. Same call as `status:wallet` — retire when the gate is gone, keep
     the id only while it still gates something.
     ⭐ Safe: `revealedSurfaces` is only ever QUERIED by surface, never iterated
     as truth, so an already-banked "menu:settings" string is inert.
     ⚠️ NOT the same as `menu:notifications` below, which still gates a real
     thing (the notification FEED) and therefore keeps its id. */
  // The account menu behind the avatar, and the Roadmap's Domain track.
  | 'roadmap:domains' | 'menu:notifications'
  /* ⚠️⚠️ **THE ROUTE TO A DOMAIN'S OWN PAGE — NOT A RAIL ITEM.** The rail has no
     domain entry; `Navigation` never queries this. It gates the two doors the
     owner named (2026-08-21): Status's "Open domain" button, and the Build
     page's second-click-to-open on a domain card.
     ⚠️ Named `nav:domain` after the VIEW id it leads to (`domain`), matching how
     every other route-shaped surface is keyed.
     ⚠️⚠️ **HIDE THE AFFORDANCE, NEVER REFUSE THE CLICK.** Reveal is not a gate:
     the arrow badge and the second-click behaviour must disappear TOGETHER. An
     arrow that is still drawn and no longer works is a lock icon without the
     icon — the exact thing this file forbids. The page stays reachable by URL. */
  | 'nav:domain'
  /* ⚠️⚠️ **STATUS'S OTHER LENSES (owner, 2026-08-21).** Status arrives at step 3
     with its OVERVIEW TAB ONLY, and Overview alone cannot mean that while three
     of its five tabs are unmapped — unmapped fails OPEN, so Progress, Activity
     and Profile would have arrived with it. `achievements` was the only gated
     one (`roadmap:achievements`, step 7) and it kept its historical id.
     ⚠️ Named `status:` because that is where they live and always have — unlike
     the `roadmap:` four, these ids are NEW, so there is no banked key to honour. */
  | 'status:progress' | 'status:activity' | 'status:profile'
  /* ⭐ Settings SUBSECTIONS that only mean something once a domain is live
     (owner, 2026-08-13). Settings itself is ungated — these three are the parts
     of it that describe work the user has not done yet: how progress is shown,
     what the domains look like, and how the day is shaped.
     ⚠️ Named for what the user sees, not for the store behind them. */
  | 'settings:progress' | 'settings:categories' | 'settings:location-time';

/**
 * Surface → the Growth Module step that reveals it.
 *
 * ⚠️⚠️ Values are STEP IDS, never numbers. Renumbering the module must not
 * silently repoint a reveal (`number` is presentation; `id` is the key).
 *
 * ⚠️ **THE `// step N` COMMENTS BELOW ARE A READING AID AND THEY ROT.** `number`
 * is presentation and the module has been reordered twice; before the
 * 2026-08-11 reorder several of these were ALREADY off by one (`build-path`
 * read "step 5" while being step 4, and `first-progress` read "step 9" on one
 * line and "step 8" on another). Nothing reads them — the mapping is by ID —
 * so a wrong one misleads a human and breaks nothing. **Trust the id; treat the
 * number as a hint, and re-check it after any reorder.**
 */
export const REVEALED_BY: Record<RevealSurface, string> = {
  // ⭐ The Roadmap itself is never hidden — it is where the module lives, so
  // hiding it would hide the only thing that explains the app.
  // ⭐ Home, and the two pages that came back to the rail with the Roadmap
  // (owner, 2026-09-29). Each arrives for the step that uses it, the rule the
  // whole module follows — see the union for why Home may be gated again.
  'nav:dashboard':   'build-path',       // step 4 — with the Home components
  'nav:calendar':    'build-path',       // step 4 — for step 5, which designs the day on it
  'nav:inbox':       'rhythm',           // step 5 — for step 6, which works the funnel
  'nav:action':      'current-self',     // step 3 — a domain is active, so build in it
  // ⚠️ MOVED 14 → 13 (owner, 2026-08-21): the page arrives with the feed,
  // People and Chats, one step before the step that asks the user to look.
  'nav:discover':    'live-cycle',       // step 13 (an ACK — see DEADLOCK below)

  /**
   * ⚠️ MOVED 7 → 5 → 6 (owner, 2026-08-21, in two passes). It landed on step 5
   * first; the owner walked it back one, and step 6 is where it belongs: step 7
   * is "Trace Your Progress", whose instruction is *open Status and find where
   * it was recorded*, so these three arrive on the step directly before the step
   * that reads them — the rule the whole module follows.
   * ⭐ It also gives step 6 something to open. It had none after the Inbox panel
   * moved to step 5, which was a real state but a thin one.
   */
  'roadmap:achievements': 'process-inbox',   // step 6 — for step 7
  /**
   * ⚠️⚠️ STEP 1, NOT STEP 2. Step 2's own work — the Foundation flow — happens
   * ON this track, so revealing it at step 2 means needing the track to
   * complete the step that reveals the track. It only escaped
   * `deadlockedSurfaces()` while step 2 still (wrongly) pointed at Reflect and
   * was therefore absent from `STEP_WORKS_ON`; repointing it to the Roadmap
   * turned a latent trap into a live one.
   */
  'roadmap:assessments':  'clear-the-mind',  // step 1 — before the step that uses it
  /**
   * ⚠️ MOVED 16 → 19 (owner, 2026-08-21). Step 20 is Explore Core Values; step
   * 19 (`marketplace`) is the step before it and an ack, so reveal can see it.
   * ⚠️⚠️ NEVER `core-values` (20): that step's work is on this very track.
   */
  'roadmap:values':       'marketplace',     // step 19 — for step 20

  /**
   * ⭐⭐ THE REST OF THE LIBRARY, ONE ASSESSMENT AT A TIME (Calibration, owner
   * 2026-10-05). These replace `roadmap:assessment-library` (`join-guild`, step
   * 15), which opened all ten at once. Each is revealed by the Calibration step
   * BEFORE the one about it — the deadlock rule: a step whose work is ON a
   * surface cannot be the step that reveals it (`STEP_WORKS_ON` names each).
   *
   * ⚠️ The library key's long history (it moved 7 → 15, and needed the
   * banked-reveal mechanism to reach `join-guild`) is in git; what survives of it
   * is `LEGACY_LIBRARY_SURFACE` below — the accounts that banked it keep all ten.
   */
  'roadmap:assessment-identity':     'cal-begin',            // C0 — for C1
  'roadmap:assessment-life-balance': 'cal-north-star',       // C1 — for C2
  'roadmap:assessment-energetic':    'cal-life-balance',     // C2 — for C3
  'roadmap:assessment-mind':         'cal-energetic',        // C3 — for C4
  'roadmap:assessment-job-role':     'cal-mind',             // C4 — for C5
  'roadmap:assessment-career':       'cal-job-role',         // C5 — for C6
  'roadmap:assessment-personality':  'cal-career-compass',   // C6 — for C7
  'roadmap:assessment-archetype':    'cal-personality',      // C7 — for C8
  'roadmap:assessment-shadow':       'cal-archetype',        // C8 — for C9
  'roadmap:assessment-growth':       'cal-shadow',           // C9 — for C10
  /**
   * ⭐ Talent, one step before step 8 introduces it. Step 8's own work is
   * LOOKING at (and now taking) this assessment, so it cannot be revealed by
   * step 8 — `STEP_WORKS_ON['meet-your-talents']` names it and the deadlock
   * check enforces it.
   */
  'roadmap:assessment-talent': 'first-progress',   // step 7 — for step 8

  /**
   * ⭐ The Life Map alone, one step ahead of the step that needs it. Step 3
   * completes on having TAKEN it, so it cannot ride with the library (step 7)
   * — and it cannot ride with step 3 either, for the same reason `know-thyself`
   * could not reveal the library. Step 2 is the last moment before it is needed.
   *
   * ⚠️ This is why step 2 shows Foundation and NOTHING else: the Life Map turns
   * up as the user arrives at step 3, and the other ten at step 7.
   */
  'roadmap:assessment-lifemap': 'purpose',     // step 2

  /* ⚠️⚠️ THESE FIVE NO LONGER MOVE TOGETHER (owner, 2026-08-21). The page and
     its first three tabs arrive at step 13; Guilds stays at 14 for step 15, and
     the Marketplace waits for step 17. Each tab now names the step that makes it
     worth opening rather than inheriting one from its neighbours. */
  'discover:global':      'live-cycle',      // step 13
  'discover:people':      'live-cycle',      // step 13
  'discover:chats':       'live-cycle',      // step 13
  // ⚠️ Step 15 JOINS a guild, so the tab must precede it — and `join-guild` is
  // fetch-backed, so it could not be the trigger even if it read better.
  'discover:guilds':      'discover-people', // step 14 — for step 15
  /**
   * ⚠️⚠️ **STEP 18, AND IT IS BANKED — see `roadmap:assessment-library`.** This
   * sat on step 17 because `protocols`, like `join-guild`, is fetch-backed and
   * the rail's producer can never observe it. Since the module page began
   * banking what its fetched states earn, both of those steps can carry a
   * surface, and this one moved to the step the owner asked for.
   *
   * ⭐ Safe here for the same reason the library is: this is a TAB inside
   * Discover, not a rail destination. A banked surface arrives when the module
   * page next renders — fine for something the module itself sends you to at
   * step 19, and wrong for anything the rail draws (a test enforces that).
   * ⚠️ Step 19's own button opens the Marketplace VIEW directly, so the step is
   * never unreachable even in the window before the bank lands.
   */
  'discover:marketplace': 'protocols',       // step 18 — for step 19

  /**
   * ⭐⭐ THE REFLECT PAGE ARRIVES FOR THE STEP THAT ASKS FOR AN ENTRY (owner,
   * 2026-08-21). Step 11 is "How Are You Feeling?", so the page turns up as its
   * turn comes: revealed by step 10's completion, on screen for the whole of
   * step 11.
   *
   * ⚠️⚠️ NEVER `first-reflection` ITSELF. That step completes by WRITING an
   * entry, which happens on this page — the textbook deadlock this file exists
   * to refuse. `deadlockedSurfaces()` proves it, via `STEP_WORKS_ON` below.
   *
   * ⚠️ THE CONSEQUENCE THE OWNER SHOULD SEE: Assessments, the Life Map and
   * Values all LIVE on Reflect since the 2026-08-19 IA reorg, and they are
   * revealed at steps 1, 2 and 16. So from step 1 to step 9 a reveal-enrolled
   * user has surfaces whose labels read "…, on Reflect" while Reflect is not on
   * the rail. Nothing is unreachable — every one of those steps opens the page
   * with its own button — but the chip prose names a place the rail does not
   * show. Moving this to `clear-the-mind` (step 1) is the one-line alternative.
   */
  /**
   * ⚠️⚠️ **MOVED 10 → 1 (owner, 2026-08-21).** It was revealed by step 10 so that
   * it arrived for step 11, the step that asks for a journal entry. That left
   * the consequence flagged when it was written: **Assessments, the Life Map and
   * Values all live ON this page** since the 2026-08-19 IA reorg, and they are
   * revealed at steps 1, 2 and 16 — so steps 1 to 9 named a page the rail did
   * not show. The page now arrives WITH the first thing that lives on it.
   * ⭐ The Journal TAB still arrives at step 11 — see `reflect:journal`.
   */
  'nav:journal':      'clear-the-mind',      // step 1 — arrives with Assessments

  /**
   * ⭐ The Journal TAB, for the step that asks for an entry. The PAGE arrives at
   * step 1 (`nav:journal`); this is the tab inside it.
   * ⚠️⚠️ NEVER `first-reflection` — that step completes by writing an entry IN
   * this tab. `STEP_WORKS_ON` names it and `deadlockedSurfaces()` enforces it.
   */
  'reflect:journal':  'ai-assistant',        // step 10 — on screen for step 11
  // ⚠️ MOVED 11 → 12 (owner, 2026-08-21). Step 13 lives a whole cycle; step 12
  // is the step before it, so the tab arrives as it becomes the subject.
  'reflect:cycles':   'work-the-day',        // step 12 — for step 13
  // ⚠️ MOVED 11 → 17 (owner, 2026-08-21) — six steps of a Protocol tab nothing
  // asked for. Step 18 is the Protocols step; `domain-areas` (17) is the step
  // before it, and an ack, so reveal can see it.
  // ⚠️⚠️ NEVER `protocols` (18) ITSELF: that step's work is IN this tab, and it
  // is also one of the two fetch-backed steps reveal can never observe.
  'reflect:protocol': 'domain-areas',        // step 17 — for step 18

  /* ⚠️ These are PANELS, not routes. Hiding the Inbox panel never hides the
     Inbox: step 2 sends you there with its own "Open Inbox" button, because the
     Growth Module is itself the navigation for a user this early. The same is
     true of all four — every one of them is reachable by a step's own button
     long before its panel turns up on Home.
     ⭐ EACH IS REVEALED BY THE STEP THAT PRODUCES WHAT IT SHOWS, which is why
     none of them can deadlock: a panel that DISPLAYS a thing is never the place
     the thing is MADE. Actions are built on the Build page, templates on the
     Calendar, and the day is worked through the schedule panel — which is why
     `home:schedule` arrives at step 5, seven rows before step 12 needs it. */
  // ⚠️ MOVED 6 → 5 (owner, 2026-08-21): the panel is on Home before step 6 asks
  // the user to work the funnel it summarises.
  'home:inbox':    'rhythm',                 // step 5 — for step 6
  // ⚠️ `build-path`'s own work is on `nav:action` (the Build page), NOT here —
  // this panel only shows what that page created.
  'home:actions':  'build-path',             // step 4
  // ⚠️ `rhythm`'s work is on the CALENDAR (`surface: 'calendar'`), not on this
  // panel, so revealing it here is safe. ⭐ It must land no later than step 12,
  // whose work IS on this panel — see `STEP_WORKS_ON['work-the-day']`.
  // ⚠️ MOVED 5 → 4 (owner, 2026-08-21): step 5 designs the day, so the panel it
  // is designed for is on screen while that happens.
  'home:schedule': 'build-path',             // step 4 — for step 5
  /**
   * ⚠️ MOVED 12 → 4 (owner, 2026-08-21). It gates the clock AND the day-progress
   * ring, and both describe a day that has actions in it — which is what step 4
   * produces. Waiting until step 12 left the top of Home empty for eight steps.
   */
  'home:clock':    'build-path',             // step 4 — a day with actions in it
  /**
   * ⭐⭐ **FOCUS AND FREE MODE — A NEW SURFACE (owner, 2026-08-21), NOT A RENAME.**
   * The ask was to rename "Home's clock and day progress" to "Focus/Free Mode"
   * and move it to step 11. Those are not the same thing: `home:clock` gates the
   * ClockWidget and the completion RING, while Focus and Free are the two mode
   * buttons in `SchedulePanel`'s header. Relabelling the ring would have put a
   * true-sounding name on the wrong control — the same class of false claim as
   * step 5's "build a day template on the Calendar".
   * ⭐ So the modes became a surface of their own, revealed at step 11 so they
   * are on screen for step 12 ("Navigate Your Day"), which is what the ask was
   * actually for.
   * ⚠️⚠️ NEVER `work-the-day` (12) itself: `STEP_WORKS_ON` names this as that
   * step's work, so revealing it there is the textbook loop.
   */
  'home:day-mode': 'first-reflection',       // step 11 — for step 12

  // ⚠️ MOVED 10 → 9 (owner, 2026-08-21): the assistant is revealed by the step
  // BEFORE the one that introduces it, so step 10 can actually open it.
  // ⭐ `personalize` is an ack — persisted, no fetch — so reveal can see it.
  'nav:assistant': 'personalize',            // step 9 — for step 10
  /**
   * ⚠️⚠️ **MOVED 10 → 1 ON 2026-08-21 (owner), REVERSING A DELIBERATE PAIRING.**
   * This read `ai-assistant` and the note beside it said "coin arrives WITH the
   * assistant, because the assistant is the first thing that spends it — a
   * balance with nothing to spend it on explains nothing." That reasoning was
   * about SPENDING. It was answered by the fact that the sign-up Coin is granted
   * server-side at first load, so a new user HAS a balance from step 0 — the
   * wallet was hidden for ten steps while holding real money.
   *
   * ⭐ Step 1, not step 2, so the wallet is on the rail AS the user arrives at
   * step 2 — which is where the sign-up Coin is now announced (`StepFooter`'s
   * `WelcomeCoin`). A step whose copy says "already in your wallet" while the
   * wallet is not yet on screen would be telling the user about a place they
   * cannot see.
   */
  'nav:wallet':    'clear-the-mind',         // step 1 — on screen for step 2
  /**
   * ⚠️ The avatar block is the ONLY route to Status (the rail has no Status
   * entry), so this reveals the page as well as the block.
   * ⚠️⚠️ **MOVED 7 → 3 (owner, 2026-08-21).** Step 3 activates a domain, which is
   * the first moment the character sheet describes anything, so Status arrives
   * there — with its OVERVIEW TAB ONLY. Its other four lenses stay behind their
   * own steps, which is why three of them had to become surfaces at all.
   * ⭐ Not a deadlock: step 3's own work is the Life Map
   * (`STEP_WORKS_ON['current-self']`), not this block.
   */
  'nav:avatar':    'current-self',           // step 3 — Overview only

  // ⚠️ `domain-areas` is an ACK step, so this cannot deadlock even though the
  // track and the step are about the same thing.
  // ⚠️ MOVED 17 → 16 (owner, 2026-08-21), so the track is on screen for step 17
  // rather than arriving as its reward. It also stops this step revealing the
  // very track it is about — which was only ever safe because it is an ack.
  'roadmap:domains':    'know-thyself',      // step 16 — for step 17
  /**
   * ⭐ The domain pages themselves, revealed by step 16 so both doors are on
   * screen for step 17 ("Explore Domain Areas") — owner, 2026-08-21.
   * ⚠️ NOT `domain-areas` (step 17): that step's work is exploring a domain
   * page, so revealing the route from it would be the textbook loop.
   */
  'nav:domain':         'know-thyself',      // step 16 — for step 17
  /* ⚠️ `menu:settings` was here, revealed by `personalize` (step 9). Retired
     2026-08-13 — see the union above. Settings is no longer gated at all. */
  /* ⚠️ ALL THREE ON `current-self` (step 3) — the step that activates a domain.
     Before it there are no domains to recolour, no progress worth displaying
     and no day to shape, so these panels would describe nothing.
     ⭐ Not a deadlock: step 3's own work is on `roadmap:assessment-lifemap`
     (see STEP_WORKS_ON), so none of these is the surface it needs to finish. */
  'settings:progress':      'current-self',  // step 3
  'settings:categories':    'current-self',  // step 3
  'settings:location-time': 'current-self',  // step 3

  /* ⭐ Status's lenses, opening as the work they display starts to exist.
     ⚠️ Progress and Activity ride `first-progress` (step 7) — the step whose
     whole subject is Status, and whose own rule is "complete one action", so
     neither tab is needed to finish it. Overview carries the status section, so
     step 7's "open Status and find where it was recorded" is satisfied by the
     tab the user already has.
     ⚠️ Profile rides `discover-people` (step 14), the same reasoning as
     `menu:notifications` below: a public profile is for other people, and until
     step 14 there are none. */
  // ⚠️ These two move WITH Achievements, as they always have — the three Status
  // lenses arrive together or the ribbon fills in pieces.
  'status:progress': 'process-inbox',        // step 6 — for step 7
  'status:activity': 'process-inbox',        // step 6 — for step 7
  // ⚠️ MOVED 14 → 13 (owner, 2026-08-21), with the Discover page it belongs to:
  // a public profile is worth having the moment there are people to see it.
  'status:profile':  'live-cycle',           // step 13 — for step 14

  // ⚠️ MOVED 14 → 13 with the page it belongs to (owner, 2026-08-21).
  'menu:notifications': 'live-cycle',        // step 13 — nothing notifies you
                                             // until there are other people.
                                             // ⭐ This gates the notification
                                             // FEED, and now also the in-app
                                             // category toggles in Settings →
                                             // Notifications, which configure
                                             // that very feed. Email and Push
                                             // are NOT behind it: they work
                                             // from day one.
};


/**
 * ⚠️⚠️ **THE SIGNALS REVEAL IS ALLOWED TO ASK FOR — a deliberate SUBSET of
 * `JourneySignals`.**
 *
 * `useJourneySignals` FETCHES (guilds and protocols), and reveal is computed by
 * the nav rail on every screen, so it may never use that hook. This is the part
 * that comes from persisted or local stores and nothing else. A signal added
 * here must be readable without a network call, or the rail starts fetching on
 * every app load.
 */
export interface RevealSignals {
  /** Persisted acknowledgements — `userStore.moduleAcks`. */
  acks: readonly string[];
  /** Items ever captured into the Inbox. */
  inboxCaptured: number;
  /** Captures that have LEFT the funnel (`status !== 'open'`). */
  inboxProcessed: number;
  /** Day templates the user has built. */
  dayTemplates: number;
  purposeStatement: string;
  visionStatement: string;
  missionStatement: string;
  hasLifeMap: boolean;
  /** Domains both MAPPED and carrying an authored mission. */
  missionedDomains: number;
  /** A Quest → Goal → linked-action cascade exists. */
  hasLinkedCascade: boolean;
  /** At least one COMPLETED action in the activity log. */
  actionsCompleted: number;
  /** AUTHORED journal entries — the store seeds 7 permanent demo rows. */
  journalEntries: number;
}

/**
 * Which module steps reveal can see as completed in order, now or historically.
 *
 * ⚠️⚠️ **THIS USED TO LIVE INSIDE `useReveal` AND COMPUTED ONLY SEVEN STEPS,
 * WHICH SILENTLY STRANDED EIGHT SURFACES** (found 2026-08-21). `REVEALED_BY`
 * named `clear-the-mind`, `rhythm`, `process-inbox`, `ai-assistant`,
 * `work-the-day` and `domain-areas`; the hook could produce none of them, so
 * `roadmap:assessments`, `home:inbox`, `home:schedule`, `home:clock`,
 * `nav:assistant`, `nav:wallet` and `roadmap:domains` could never be earned by
 * a reveal-enrolled user. Not hidden-for-a-while — hidden FOREVER, with no
 * reachable requirement, which is the exact failure of the 2026-08-03 gate this
 * whole file exists to avoid.
 *
 * ⚠️⚠️ **THE TEST SUITE WAS GREEN THROUGHOUT.** "every mapped step is one a user
 * can actually complete" checked `REVEALED_BY` against `MODULE_STEPS` — every id
 * really did exist — and "finishing every step reveals every surface" fed
 * `isRevealed` a completedSteps list the hook could never produce. Both asserted
 * the MAP and neither asserted the PRODUCER. `everyRevealStepIsComputable()`
 * below is the missing assertion.
 *
 * ⭐ Pure and exported precisely so it can be tested without rendering the rail.
 * ⚠️ Every rule here MIRRORS `deriveStepState` and must keep mirroring it — same
 * constants, same comparisons. Two doors onto "is this step done" that ask
 * different questions is a bug the module has already had.
 */
export function completedRevealSteps(s: RevealSignals, reached: readonly string[] = []): string[] {
  const done: string[] = [];

  // Step 1 — ⚠️ `>= BRAIN_DUMP_TARGET`, the same threshold the step's own
  // readout prints. "Some captures" is not the rule.
  if (s.inboxCaptured >= BRAIN_DUMP_TARGET) done.push('clear-the-mind');
  // Step 2 — all three statements.
  if ([s.purposeStatement, s.visionStatement, s.missionStatement]
    .every(t => t.trim().length > 0)) done.push('purpose');
  // Step 3 — the merged rule: a life map AND a domain with a written mission.
  if (s.hasLifeMap && s.missionedDomains > 0) done.push('current-self');
  // Step 4 — a Goal with something actually attached to it.
  if (s.hasLinkedCascade) done.push('build-path');
  // Step 5 — one day template exists.
  if (s.dayTemplates > 0) done.push('rhythm');
  // Step 6 — ⚠️ PROCESSED, not triaged: parked and archived count, because a
  // brain dump that was mostly thoughts never reaches `routed`.
  if (s.inboxProcessed >= INBOX_PROCESSED_TARGET) done.push('process-inbox');
  // Step 7 — ⚠️ `level_up` is not an action anyone took (`isCompletedAction`).
  if (s.actionsCompleted > 0) done.push('first-progress');
  // Step 11 — ⚠️ AUTHORED entries only.
  if (s.journalEntries > 0) done.push('first-reflection');

  /**
   * Every ACK step, from the persisted list.
   *
   * ⚠️⚠️ This was TWO HARD-CODED ACKS (`discover-people`, `know-thyself`) and
   * that is how step 10 went missing: `ai-assistant` is an ack, its id sits in
   * `moduleAcks` the moment the user ticks it, and nothing read it — so the
   * assistant button and the wallet it was paired with were mapped to a step
   * reveal could not see.
   * ⚠️ `know-thyself` still means THE ACK, never the Talent assessment: the step
   * was reframed to the library on 2026-08-18, and reading the quiz would reveal
   * the Values track for work done at step 8.
   */
  for (const id of [...ACK_STEP_IDS, ...CALIBRATION_ACK_IDS]) {
    if (s.acks.includes(id)) done.push(id);
  }

  // History is also completion evidence here: reveal cannot fetch guilds or
  // protocols, and already-earned surfaces must survive deleted artifacts.
  // Rows use their live own state instead; both use the same ordering rule.
  const evidence = new Set([...done, ...reached]);
  const states = sequenceStepStates(id => evidence.has(id) ? 'completed' : 'available', reached);
  return ALL_JOURNEY_STEPS.filter(step => states[step.id] === 'completed').map(step => step.id);
}

/** Signals for a user who has done everything reveal can observe. */
export function allRevealSignalsMet(): RevealSignals {
  return {
    acks: [...ACK_STEP_IDS, ...CALIBRATION_ACK_IDS],
    inboxCaptured: BRAIN_DUMP_TARGET,
    inboxProcessed: INBOX_PROCESSED_TARGET,
    dayTemplates: 1,
    purposeStatement: 'p', visionStatement: 'v', missionStatement: 'm',
    hasLifeMap: true, missionedDomains: 1,
    hasLinkedCascade: true, actionsCompleted: 1, journalEntries: 1,
  };
}

/**
 * Steps that reveal cannot compute itself, but which the Growth Journey page can
 * BANK on its behalf.
 *
 * ⭐⭐ **THE SECOND GRANTING PATH (2026-08-21).** `completedRevealSteps` may never
 * fetch, because the nav rail runs it on every screen — so `join-guild` and
 * `protocols`, whose completion is a network read, were unreachable for reveal.
 * `useJourneySignals` (Home and Roadmap) fetches those signals and banks
 * completed-in-order IDs. This producer can then cross those steps without a
 * fetch. GrowthJourneyTrack also banks surfaces directly from its sequenced
 * states; isRevealed continues to honour the existing surface bank.
 *
 * ⚠️⚠️ **BANKED IS NOT THE SAME AS COMPUTED, AND THE DIFFERENCE IS TIMING.** A
 * computed surface appears when its artifact exists AND its step is reached.
 * A fetch-backed one needs Home or Roadmap to have observed it in order. For
 * that reason this list is EXPLICIT rather than "everything else": mapping a rail-critical
 * surface here would make it arrive late, silently.
 */
export const BANKED_ONLY_STEPS: readonly string[] = ['join-guild', 'protocols'];

/**
 * ⭐⭐ **THE INVARIANT THAT WAS MISSING: every step a surface is mapped to must be
 * one something can actually produce** — the rail's own producer, or the module
 * page's banker. Returns the offenders, so a test names them.
 *
 * ⚠️ A step can be real, spelled right, and present in `MODULE_STEPS` while still
 * being invisible to reveal. That combination passed every existing test.
 * ⚠️⚠️ The banker covers only `BANKED_ONLY_STEPS`. Widening this to "any real
 * step" would make the check vacuous — which is exactly what it was written to
 * stop, so the two lists must stay separate and both must stay small.
 */
export function unreachableRevealSteps(): string[] {
  const computable = new Set([
    ...completedRevealSteps(allRevealSignalsMet(), BANKED_ONLY_STEPS),
    ...BANKED_ONLY_STEPS,
  ]);
  return [...new Set(Object.values(REVEALED_BY))].filter(step => !computable.has(step));
}

/**
 * ⚠️⚠️ **DEADLOCK — A SURFACE MAY NOT BE REVEALED BY WORK DONE ON IT.**
 *
 * ⭐ SCOPE: only TABS and PANELS can deadlock. A step whose work happens on a
 * whole VIEW is always reachable, because the module's own step card carries an
 * "Open <surface>" button that calls `setCurrentView` regardless of what the
 * rail is showing. That is why `nav:assistant` ← step 6 is safe even though the
 * assistant button is hidden until step 6: step 6 opens the assistant VIEW.
 *
 * The obvious mapping (step 13 "Join a Guild" reveals the Guilds tab) is a trap:
 * you cannot join a guild without the Guilds tab, and the tab does not appear
 * until you have joined. The surface is then unreachable FOREVER, which is
 * exactly the old gate's *"Complete requirements to unlock!"* with no
 * requirement that could be met.
 *
 * Five of the first-draft mappings had it — guilds, marketplace, protocol,
 * journal, and `nav:action` (build a cascade to reveal the Build page).
 *
 * This map is what makes the rule TESTABLE: it records, for each step whose work
 * happens on a revealable surface, which surface that is. A step here may never
 * be the thing that reveals its own surface.
 *
 * ⭐ AN ACK STEP MAY BE EXEMPT — IT IS NOT AUTOMATICALLY EXEMPT. The exemption
 * exists for `domain-areas`, which REVEALS the very track it is about: that is
 * only safe because its button lives in the module, so the track is never needed
 * to tick it. Such a step must be ABSENT here, or `deadlockedSurfaces()` would
 * (correctly) flag it.
 *
 * ⚠️⚠️ `marketplace` is the other shape: an ack that MEANS "I went and looked".
 * The tab genuinely is needed, so it stays MAPPED — and being mapped is the
 * safer state, because the walkability test then proves the tab is already
 * visible when step 17 comes round. Absence is the dangerous state, not presence.
 */
export const STEP_WORKS_ON: Record<string, RevealSurface> = {
  // ⚠️ The LIFE MAP, not the whole track. Step 3 completes on taking that one
  // assessment; naming the track would say the step needs everything on it.
  'current-self':     'roadmap:assessment-lifemap',
  // ⚠️ Step 2's work is the Foundation flow, which lives on the Assessments
  // track. Absent from this map until 2026-08-06 only because the step pointed
  // at Reflect — a dead link, and a deadlock hiding behind it.
  'purpose':          'roadmap:assessments',
  'build-path':       'nav:action',
  'live-cycle':       'reflect:cycles',
  // ⚠️ `know-thyself` is NOT here any more (2026-10-05): its work is opening the
  // Calibration module, which is always listed (a locked teaser before step 16)
  // and has no reveal key.
  // ⭐ Each Calibration step works on its OWN assessment — which is exactly why
  // each key is revealed by the step before (the walkability test proves it).
  'cal-north-star':      'roadmap:assessment-identity',
  'cal-life-balance':    'roadmap:assessment-life-balance',
  'cal-energetic':       'roadmap:assessment-energetic',
  'cal-mind':            'roadmap:assessment-mind',
  'cal-job-role':        'roadmap:assessment-job-role',
  'cal-career-compass':  'roadmap:assessment-career',
  'cal-personality':     'roadmap:assessment-personality',
  'cal-archetype':       'roadmap:assessment-archetype',
  'cal-shadow':          'roadmap:assessment-shadow',
  'cal-growth-maturity': 'roadmap:assessment-growth',
  // ⚠️ TALENT, not the whole library. Step 8 takes that ONE assessment; naming
  // the library made the step depend on ten it never touches — and that is what
  // pinned the library to step 7 for six weeks.
  'meet-your-talents': 'roadmap:assessment-talent',
  /* ⚠️⚠️ REPOINTED FROM `nav:dashboard` ON 2026-08-19, not deleted with it.
     Step 12's declared work is Focus and Free mode, and BOTH are controls on
     Home's SCHEDULE panel — read its `instructions`. Naming Home the VIEW was
     always the coarser truth; now that the panel is mapped in its own right,
     naming the panel is the real one.
     ⭐ Keeping it mapped is the point: `home:schedule` arrives at step 5, so the
     walkability test now PROVES the panel is on screen seven rows before this
     step asks the user to switch it to Free mode. Dropping the entry would have
     retired that proof at the exact moment it started being worth something —
     absence is the dangerous state, not presence. */
  /* ⭐ RE-DECLARED 2026-08-21. `first-reflection` named `reflect:journal` until
     that surface was retired on 2026-08-19, and the entry went with it —
     correctly at the time, because an always-visible surface cannot deadlock so
     the entry proved nothing. Now that the PAGE is revealable the entry buys
     something again: the walkability test replays the module and proves Reflect
     is on screen before the step that asks for an entry, and `deadlockedSurfaces`
     refuses any future attempt to reveal the page from the entry written on it. */
  /* ⚠️ The TAB, not the page. Step 11 writes an entry in `reflect:journal`;
     naming `nav:journal` (revealed at step 1) would prove far less. */
  'first-reflection': 'reflect:journal',
  /* ⚠️⚠️ REPOINTED AGAIN 2026-08-21: `home:schedule` → `home:day-mode`. Step
     12's declared work is Focus and Free mode, and those are now a surface in
     their own right rather than two buttons inside a panel with a broader name.
     Naming the panel was the coarser truth; naming the controls is the real one,
     and the walkability test now proves the MODES are on screen at step 12
     rather than merely the panel that contains them. */
  'work-the-day':     'home:day-mode',
  'protocols':        'reflect:protocol',
  'join-guild':       'discover:guilds',
  'marketplace':      'discover:marketplace',
  'core-values':      'roadmap:values',
  /**
   * ⚠️ Step 17 is an ACK — "I went and looked at a domain's areas" — and looking
   * needs a domain page to look at, so the surface IS needed. Mapped for the
   * `marketplace` reason: presence buys the walkability proof that both doors
   * are on screen when step 17 arrives. Absence is the dangerous state.
   * ⭐ Safe: it reveals `roadmap:domains`, a different surface.
   */
  'domain-areas':     'nav:domain',
};

/**
 * Human names for the step page's "What this opens up" panel.
 *
 * ⚠️⚠️ THIS PANEL IS NOT A REWARD LEDGER, AND THAT SURVIVES RE­VEAL GOING
 * UNCONDITIONAL (2026-08-21). It used to rest on "reveal is opt-in, so these
 * were always visible anyway"; now they genuinely do arrive with the step. The
 * copy rule is unchanged for a better reason: a surface is REACHABLE BY URL and
 * by the module's own buttons the whole time, so "you have unlocked X" would
 * still be false — the step makes it worth using, it does not grant it.
 *
 * ⚠️ Several surfaces share one step (step 14 opens the whole Discover page).
 * `revealedByStep` returns them in `REVEALED_BY` order so the panel reads as a
 * list rather than a random set.
 */
/**
 * ⭐⭐ **THE CONVENTION (owner, 2026-08-21), so the next label matches:**
 *  1. **Title Case.** "The Build Page", not "the Build page". Short words inside
 *     a title stay lowercase — "The Rest of the Assessment Library".
 *  2. **A tab says where it lives, with "on":** "Protocols, on Reflect",
 *     "Guilds, on Discover". "On" is the app's idiom for a page you are looking
 *     at; "in" reads like a container you have entered, which is what Settings
 *     is and Reflect is not.
 *  3. **Several surfaces that arrive together may share ONE label** — Home's
 *     three panels are all "Home Components". `StepOpens` prints each distinct
 *     label once, and a test requires a shared label to share a step, because
 *     the same chip at two points in the journey would mean two things.
 */
export const SURFACE_LABELS: Record<RevealSurface, string> = {
  'nav:action': 'The Build Page',
  'nav:discover': 'The Discover Page',
  'roadmap:achievements': 'Achievements, Progress, Activity, on Status',
  /* ⚠️⚠️ THESE FOUR SAY "REFLECT" WHILE THEIR IDS SAY "ROADMAP", AND BOTH ARE
     CORRECT. The id is a storage key frozen where it was born (see the union);
     the label is what the step footer prints under "Also opens", so it must
     name where the user will actually find the thing. Letting the label rot to
     match the id would send people to a page these have not been on since
     2026-08-19. */
  'roadmap:assessments': 'Assessments, on Reflect',
  'roadmap:values': 'Values, on Reflect',
  'roadmap:assessment-identity': 'The North-Star Identity Assessment',
  'roadmap:assessment-life-balance': 'The Life Balance Assessment',
  'roadmap:assessment-energetic': 'The Energetic Alignment Assessment',
  'roadmap:assessment-mind': 'The Mind Assessment',
  'roadmap:assessment-job-role': 'The Job Role Assessment',
  'roadmap:assessment-career': 'The Career Compass Assessment',
  'roadmap:assessment-personality': 'The Personality Assessment',
  'roadmap:assessment-archetype': 'The Archetype Matrix Assessment',
  'roadmap:assessment-shadow': 'The Shadow Work Assessment',
  'roadmap:assessment-growth': 'The Growth Maturity Assessment',
  'roadmap:assessment-talent': 'The Talent Assessment',
  'roadmap:assessment-lifemap': 'The Life Map Assessment',
  'roadmap:domains': 'The Growth Foundations Track',
  'nav:domain': 'Domain Areas',
  'discover:global': 'The Discover Page',
  'discover:people': 'The Discover Page',
  'discover:chats': 'The Discover Page',
  'discover:guilds': 'Guilds, on Discover',
  'discover:marketplace': 'The Marketplace, on Discover',
  'nav:journal': 'The Reflect Page',
  'reflect:journal': 'The Journal, on Reflect',
  'status:progress': 'Achievements, Progress, Activity, on Status',
  'status:activity': 'Achievements, Progress, Activity, on Status',
  'status:profile': 'Public Profile, on Status',
  'reflect:cycles': 'Growth Cycles, on Reflect',
  'reflect:protocol': 'Protocols, on Reflect',
  'home:inbox': 'Home’s Inbox Panel',
  /* ⚠️⚠️ **TWO SURFACES, ONE LABEL, ON PURPOSE (owner, 2026-08-21).** The
     actions panel and the clock/ring are announced together as "Home
     Components"; `StepOpens` prints each distinct label once, so they render as
     a single chip.
     ⭐ They stayed SEPARATE surfaces rather than being merged into one id: a
     surface id is a persisted key, and merging would orphan whichever id was
     dropped while also giving up the ability to move them apart again. Sharing
     a label is a copy decision; sharing an id would have been a data one. */
  // ⭐ Home itself shares the label: it arrives with its panels, and "Home
  // Components" is the owner's name for the whole arrival.
  'nav:dashboard': 'Home Components',
  'nav:calendar': 'The Calendar Page',
  'nav:inbox': 'The Inbox Page',
  'home:actions': 'Home Components',
  'home:schedule': 'Home Components',
  'home:clock': 'Home Components',
  'home:day-mode': 'Focus and Free Mode, on Home’s Day Schedule',
  'nav:assistant': 'The Assistant',
  'nav:wallet': 'Your Wallet',
  'nav:avatar': 'Your Status Page',
  'menu:notifications': 'Your Notifications',
  'settings:progress': 'How Progress Is Shown',
  'settings:categories': 'Your Domain Colours',
  'settings:location-time': 'Your Location and Day Settings',
};

/**
 * Every surface this step reveals, in map order. Empty for the steps that reveal
 * nothing — the panel then renders nothing rather than padding.
 *
 * ⭐ DERIVED from `REVEALED_BY`, never a second hand-written list. A parallel
 * copy is how a "reward" ends up naming a surface some other step now opens.
 *
 * ⚠️ This is the TRUTH — what the step actually opens. What the step page PRINTS
 * is `announcedByStep`, which is a subset. Reveal logic must use this one.
 */
export function revealedByStep(stepId: string): RevealSurface[] {
  return (Object.keys(REVEALED_BY) as RevealSurface[])
    .filter(surface => REVEALED_BY[surface] === stepId);
}

/**
 * Surfaces that are revealed but NOT announced on the step page (owner,
 * 2026-08-21).
 *
 * ⭐⭐ **UNDERSTATING WHAT A STEP OPENS IS SAFE; OVERSTATING IS A LIE.** That
 * asymmetry is the whole reason this list is allowed to exist. `menu:settings`
 * and `status:wallet` were RETIRED rather than silenced, and correctly so — they
 * had stopped gating anything, so their labels promised a thing that was no
 * longer given. These three still gate real sections; they are simply not worth
 * saying out loud on the step about activating a domain.
 *
 * ⚠️ All three are SUBSECTIONS of a page that is itself never gated (Settings
 * has been ungated since 2026-08-13), so a user who goes looking finds Settings
 * either way. The chips read as three separate rewards for one step, next to the
 * two that matter — the Build page and Status.
 *
 * ⚠️⚠️ **NEVER add a surface here to avoid explaining it.** If a step opens
 * something a user would look for and not find, the answer is to say so — or to
 * move the reveal. Silence is for things too small to name, not for things that
 * are awkward to name.
 */
export const UNANNOUNCED_SURFACES: ReadonlySet<RevealSurface> = new Set<RevealSurface>([
  'settings:progress',
  'settings:categories',
  'settings:location-time',
  /* ⚠️ The notification FEED, revealed with the Discover page at step 13 and not
     announced (owner, 2026-08-21). Step 13's panel names two things — the page,
     and the public profile — and notifications are neither: they live in the
     header, so folding them into "The Discover Page" would be false and listing
     them separately is the noise the owner asked to cut. */
  'menu:notifications',
]);

/**
 * Reading order for the "Also opens" chips, where it has been decided.
 *
 * ⚠️⚠️ **THE PANEL'S ORDER USED TO BE AN ACCIDENT OF THIS FILE'S LAYOUT.**
 * `revealedByStep` walks `REVEALED_BY` in key order, and that map is grouped by
 * SUBJECT — rail destinations, then tracks, then Discover tabs, then Home
 * panels. So the order a user reads was a side effect of where an entry happened
 * to sit, and regrouping the map for clarity would silently rewrite it. Step 1
 * read "Assessments · Reflect · wallet" — the page before the page it is on.
 *
 * ⭐ Listing a surface here is a decision that it comes first, in this order.
 * Anything absent keeps map order, after everything listed — so this stays a
 * short list of the places order actually matters rather than a second copy of
 * the union that has to be maintained forever.
 */
export const ANNOUNCE_ORDER: readonly RevealSurface[] = [
  // Step 1 — the page, then the tab inside it, then the wallet (owner).
  'nav:journal',
  'roadmap:assessments',
  'nav:wallet',
];

/**
 * What the step page prints under "Also opens" — `revealedByStep` minus the
 * surfaces we do not say out loud, in `ANNOUNCE_ORDER`.
 *
 * ⭐ A FILTER over the derived list, never its own list of labels: a hand-written
 * copy here is exactly the "reward panel names a surface some other step now
 * opens" failure the derivation was introduced to end.
 */
export function announcedByStep(stepId: string): RevealSurface[] {
  const rank = (surface: RevealSurface) => {
    const i = ANNOUNCE_ORDER.indexOf(surface);
    // ⚠️ Unlisted sorts AFTER everything listed, keeping its map order — a
    // stable sort is what preserves that, so do not "simplify" this to a filter.
    return i === -1 ? ANNOUNCE_ORDER.length : i;
  };
  return revealedByStep(stepId)
    .filter(surface => !UNANNOUNCED_SURFACES.has(surface))
    .sort((a, b) => rank(a) - rank(b));
}

/**
 * Surfaces whose revealing step is done ON that same surface — i.e. unreachable.
 * ⭐ Exported so a test asserts it, rather than trusting this file's comments.
 */
export function deadlockedSurfaces(): string[] {
  return (Object.keys(REVEALED_BY) as RevealSurface[])
    .filter(surface => STEP_WORKS_ON[REVEALED_BY[surface]] === surface);
}

/**
 * ⭐⭐ **THE SECOND DEADLOCK SHAPE — a step whose COMPLETION RULE needs a surface
 * that its own completion reveals.** Added L4 (2026-08-09), after the first one
 * shipped and this one still got through.
 *
 * `deadlockedSurfaces()` catches *a surface revealed by the work done ON it*, and
 * it stayed green through the real thing: step 4's rule required a **Quest**,
 * quests are created on the **Build page**, and `REVEALED_BY['nav:action']` is
 * step 4. So a reveal-enrolled user needed Build in order to earn Build. It
 * escaped because the requirement lived in `deriveStepState`, not in
 * `STEP_WORKS_ON` — the step's *declared* work was the Life Map, and the Quest
 * was an undeclared extra.
 *
 * ⚠️ So the extra requirement has to be DECLARED to be checkable. `STEP_ALSO_NEEDS`
 * is that declaration: if a step's completion rule starts depending on a surface
 * beyond its own work, name it here and this check will refuse the loop.
 *
 * ⚠️⚠️ It is EMPTY on purpose, and an empty map makes a vacuous test. The suite
 * therefore runs it against a synthetic map reproducing the pre-L4 rule and
 * asserts it FLAGS that — the inverse probe, without which "no deadlocks" would
 * prove nothing at all.
 */
export const STEP_ALSO_NEEDS: Record<string, RevealSurface> = {};

export function completionDeadlocks(
  alsoNeeds: Record<string, RevealSurface> = STEP_ALSO_NEEDS,
): string[] {
  return Object.entries(alsoNeeds)
    .filter(([stepId, surface]) => REVEALED_BY[surface] === stepId)
    .map(([stepId]) => stepId);
}

/**
 * ⭐⭐ **THE MODULE IS THE NAVIGATION FOR A NEW USER.** Every step carries its own
 * "Open <surface>" button, so no route is ever unreachable just because a rail
 * item or panel has not been revealed yet. That is what lets step 2 use the
 * Inbox while both Home and its Inbox panel are still hidden — and it is why
 * hiding a PANEL can never strand anyone.
 */
export const UNMAPPED_STAY_VISIBLE = true;

export interface RevealState {
  /* ⚠️⚠️ `enrolled` WAS HERE and is gone (owner, 2026-08-21) — reveal is no
     longer opt-in. Its doc read "the single most important property in this
     file", and it was: it made the whole mechanism inert for every account. It
     had one writer, Settings → Developer, and that panel was removed as spent.
     ⚠️ Removed rather than left as an ignored field, so no caller can pass an
     `enrolled: false` that silently does nothing. */
  /** High-water: surfaces already earned. Append-only; never shrinks. */
  earned: readonly string[];
  /** Step ids complete right now (derived by the module). */
  completedSteps: readonly string[];
  /**
   * ⚠️⚠️ FALSE UNTIL THE STORES FEEDING `completedSteps` HAVE LOADED.
   * An unhydrated account looks identical to a brand-new one — every step
   * incomplete — so without this the app would hide half its navigation on
   * every cold load. Unhydrated means UNKNOWN, and unknown shows.
   * See `hydration-baseline-false-events`: a zero baseline is hydration, not an event.
   */
  hydrated: boolean;
}

/**
 * Is this surface visible?
 *
 * ⭐⭐ FOUR WAYS TO BE VISIBLE, ONE NARROW WAY TO BE HIDDEN. Read the order:
 * every early return is a failure-open path.
 */
export function isRevealed(surface: string, s: RevealState): boolean {
  /**
   * ⚠️⚠️ **`enrolled` WAS THE FIRST GUARD AND IT IS GONE (owner, 2026-08-21).**
   * Reveal was opt-in: `revealEnrolled` was `undefined` for every account, so
   * this returned `true` for everyone and the whole mechanism was inert. Its
   * only writer was Settings → Developer, which was removed as spent — so the
   * choice was to give the flag a new home or to stop pretending reveal is an
   * experiment. The owner chose the latter: the Growth Journey governs what the
   * app shows, for everybody.
   *
   * ⚠️⚠️ THE SAFETY MODEL IS NOW THREE WAYS OPEN, NOT FOUR. Unhydrated, unmapped
   * and already-earned all still fail open, and that is what has to keep this
   * safe on its own — there is no longer an account-level escape hatch to fall
   * back on if a mapping is wrong. Treat a mapping mistake as user-visible now,
   * because it is.
   */
  // 1. We cannot know yet. Unhydrated stores report every step incomplete.
  if (!s.hydrated) return true;
  // 2. Not a surface reveal knows about.
  const step = REVEALED_BY[surface as RevealSurface];
  if (!step) return true;
  // 3. Already earned — permanent, even if the artifact behind it was deleted.
  if (s.earned.includes(surface)) return true;
  // The only way to be hidden: hydrated, mapped, never earned, and the
  // revealing step is not complete right now.
  return s.completedSteps.includes(step);
}

/**
 * Which surfaces the current step completions entitle the user to.
 *
 * ⭐ Callers union this into the stored high-water set — they never replace it,
 * because a step whose artifact was deleted must not take its surface back.
 */
export function earnedFrom(completedSteps: readonly string[]): RevealSurface[] {
  return (Object.keys(REVEALED_BY) as RevealSurface[])
    .filter(surface => completedSteps.includes(REVEALED_BY[surface]));
}

/**
 * ⚠️ Every mapped step id must be a real step, or a surface becomes
 * PERMANENTLY unreachable — the exact "no requirement that could be met" bug.
 * Exported so a test can assert it rather than trusting review.
 */
export function unknownStepIds(): string[] {
  const ids = new Set(ALL_JOURNEY_STEPS.map(s => s.id));
  return Object.values(REVEALED_BY).filter(step => !ids.has(step));
}

/**
 * ⚠️⚠️ THE RETIRED "REST OF THE LIBRARY" KEY — READ FROM THE BANK, NEVER THROUGH
 * `isRevealed`. It was revealed by `join-guild` (step 15) until Calibration
 * (2026-10-05) gave each assessment its own key. It is no longer in
 * `RevealSurface`, so `isRevealed` would FAIL OPEN on it and show all ten
 * assessments to every account — the very thing Calibration exists to stop.
 *
 * ⭐ Accounts that banked it before keep all ten (owner D4: reveal never
 * re-hides). Measured 2026-10-05: every account past step 16 had it banked.
 */
export const LEGACY_LIBRARY_SURFACE = 'roadmap:assessment-library';

export const hasLegacyLibrary = (earned: readonly string[]): boolean =>
  earned.includes(LEGACY_LIBRARY_SURFACE);

/**
 * Which reveal key shows each assessment TYPE on the Assessments library.
 *
 * ⚠️ Keyed by `assessment.type`, which is a stored value (the DB's CHECK list),
 * not a display name.
 */
export const ASSESSMENT_TYPE_SURFACE: Readonly<Record<string, RevealSurface>> = {
  life_map:     'roadmap:assessment-lifemap',
  talent:       'roadmap:assessment-talent',
  identity:     'roadmap:assessment-identity',
  life_balance: 'roadmap:assessment-life-balance',
  energetic:    'roadmap:assessment-energetic',
  mind:         'roadmap:assessment-mind',
  job_role:     'roadmap:assessment-job-role',
  career:       'roadmap:assessment-career',
  personality:  'roadmap:assessment-personality',
  archetype:    'roadmap:assessment-archetype',
  shadow:       'roadmap:assessment-shadow',
  growth:       'roadmap:assessment-growth',
};

/** The ten Calibration keys — the set the retired library key used to cover. */
const CALIBRATION_ASSESSMENT_SURFACES: ReadonlySet<RevealSurface> = new Set([
  'roadmap:assessment-identity', 'roadmap:assessment-life-balance', 'roadmap:assessment-energetic',
  'roadmap:assessment-mind', 'roadmap:assessment-job-role', 'roadmap:assessment-career',
  'roadmap:assessment-personality', 'roadmap:assessment-archetype', 'roadmap:assessment-shadow',
  'roadmap:assessment-growth',
]);

/**
 * Is an assessment of this type on the user's Assessments library?
 *
 * · An UNMAPPED type is shown — the same fail-open rule as every unmapped
 *   surface (`UNMAPPED_STAY_VISIBLE`), so a new assessment can never vanish
 *   because nobody added it here.
 * · One of the ten Calibration types is shown if the account BANKED the retired
 *   library key (owner D4) — read from `earned` directly, never `revealed()`.
 * · Otherwise its own key decides.
 */
export function isAssessmentTypeVisible(
  type: string,
  revealed: (surface: string) => boolean,
  earned: readonly string[],
): boolean {
  const surface = ASSESSMENT_TYPE_SURFACE[type];
  if (!surface) return true;
  if (CALIBRATION_ASSESSMENT_SURFACES.has(surface) && hasLegacyLibrary(earned)) return true;
  return revealed(surface);
}
